import { getFeedback } from '@sentry/react'
import { useEffect, useRef } from 'react'
import { useGetProfile } from 'api/generated/profile/profile'
import { useSentryFeedbackFlag } from 'hooks/useSentryFeedbackFlag'
import { useSelector } from 'store/hooks/useSelector'
import { isIsland } from 'utils/sentryUtility'

type ActorComponent = ReturnType<NonNullable<ReturnType<typeof getFeedback>>['createWidget']>

const selectLoggedIn = (state: Store) => state.auth.loggedIn

export function SentryFeedbackWidget() {
  const loggedIn = useSelector(selectLoggedIn)
  const isFlagEnabled = useSentryFeedbackFlag()
  // Gated on the session for the same reason as `SentryUserSync`: this widget renders
  // outside `<Routes>`, so an ungated query fetched the profile with no bearer token on
  // the login page. The effect below already ignores `profile` unless `loggedIn`.
  const { data: profile } = useGetProfile({ query: { enabled: loggedIn } })
  const widgetRef = useRef<ActorComponent | null>(null)

  useEffect(() => {
    // Wait for profile so SentryUserSync has called setSentryUser before the widget
    // appears — the dialog captures getUser() on first click and never refreshes it.
    const shouldShow = loggedIn && isFlagEnabled && isIsland && !!profile?.ProviderId

    if (shouldShow && !widgetRef.current) {
      const feedback = getFeedback()
      if (feedback) {
        widgetRef.current = feedback.createWidget()
      }
    } else if (!shouldShow && widgetRef.current) {
      widgetRef.current.removeFromDom()
      widgetRef.current = null
    }

    return () => {
      if (widgetRef.current) {
        widgetRef.current.removeFromDom()
        widgetRef.current = null
      }
    }
  }, [loggedIn, isFlagEnabled, profile?.ProviderId])

  return null
}
